import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const containerStyle = {
  width: "100%",
  height: "600px"
};

// Minimalist map styling - lighter roads, fewer icons
const mapStyle = [
  { elementType: "geometry", stylers: [{ color: "#f6f6f6" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ddd" }]
  }
];

// A small SVG path for a custom marker (pin shape), filled with #003663
// font for the marker's label is automatically white if we set label color to "#fff"
function createMarkerIcon(color = "#003663") {
  // This path is a simplified "pin" shape
  const svg = `
    <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="${color}" d="M18,0 C8.0589,0 0,8.059 0,18 C0,27.942 16.5,46.53 17.28,47.45
        a1,1 0 0 0 1.44,0C19.5,46.53 36,27.942 36,18 36,8.059 27.941,0 18,0Z"/>
    </svg>
  `;
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    // scale the pin if needed
    scaledSize: new window.google.maps.Size(36, 48),
    labelOrigin: new window.google.maps.Point(18, 19) // so the text is near the center
  };
}

const HotelMapModal = ({ hotels, isOpen, onClose }) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY // use your env var
  });

  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // If we have at least one hotel, center on its coordinates; otherwise, fallback
  const defaultPosition = hotels.length
    ? {
        lat: parseFloat(hotels[0].geolocation.latitude),
        lng: parseFloat(hotels[0].geolocation.longitude)
      }
    : { lat: 48.8566, lng: 2.3522 }; // Paris fallback

  // Build Markers once the map is loaded and hotels are available
  useEffect(() => {
    if (!mapInstance || !hotels.length) return;

    // Create markers
    const markers = hotels.map((hotel) => {
      const { latitude, longitude } = hotel.geolocation || {};
      if (!latitude || !longitude) return null;

      // Price string (e.g. "154.97 EUR")
      const priceInfo = `${hotel.offers?.[0]?.price?.amount.toFixed(2) || "?"} ${
        hotel.offers?.[0]?.price?.currency || ""
      }`;

      const marker = new window.google.maps.Marker({
        position: {
          lat: parseFloat(latitude),
          lng: parseFloat(longitude)
        },
        icon: createMarkerIcon("#003663"), // Our custom pin icon in #003663
        label: {
          text: priceInfo,
          fontSize: "12px",
          color: "#fff",
          fontWeight: "bold"
        }
      });

      // Clicking the marker navigates to details
      marker.addListener("click", () => {
        navigate(`/hotels/details/${hotel.id}`);
      });
      return marker;
    }).filter(Boolean); // Remove any null entries

    // Create cluster
    new MarkerClusterer({
      markers,
      map: mapInstance,
      renderer: {
        render: ({ count, position }) => {
          // A custom cluster icon, same color pin but with the cluster count
          const clusterIcon = createMarkerIcon("#003663");
          return new window.google.maps.Marker({
            position,
            icon: clusterIcon,
            label: {
              text: String(count),
              color: "#fff",
              fontSize: "14px",
              fontWeight: "bold"
            }
          });
        }
      }
    });
  }, [mapInstance, hotels, navigate]);

  if (!isOpen || !isLoaded) return null;

  return (
    <div
      className="modal d-block"
      onClick={onClose}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content" style={{ borderRadius: "12px" }}>
          <div className="modal-header" style={{ borderBottom: "1px solid #eee" }}>
            <h5 style={{ color: "#003663" }}>{t("Hotels on Map")}</h5>
            <button onClick={onClose} className="btn-close"></button>
          </div>
          <div className="modal-body p-0" style={{ borderRadius: "0 0 12px 12px" }}>
          <GoogleMap
  mapContainerStyle={containerStyle}
  center={defaultPosition}
  zoom={13}
  options={{
    mapTypeControl: true,
    styles: mapStyle,
    // These options hide some default UI:
    disableDefaultUI: false,
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    // ✨ Allow immediate scroll-zoom without Ctrl:
    gestureHandling: "greedy",
  }}
  onLoad={(map) => setMapInstance(map)}
/>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelMapModal;
